import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PumpAnalysis } from "@shared/schema";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/utils";
import { ResultsCharts } from "./results-charts";

interface ResultsComparisonProps {
  analysis: PumpAnalysis;
  recommendations: any[];
  analyticsData: any;
}

export function ResultsComparison({ 
  analysis, 
  recommendations, 
  analyticsData 
}: ResultsComparisonProps) {
  const { t } = useTranslation();
  const [activeComparisonTab, setActiveComparisonTab] = useState("financial");
  
  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="py-8 text-center">
        <h3 className="text-lg font-medium mb-2">{t("comparison.noRecommendations")}</h3>
        <p className="text-muted-foreground">{t("comparison.noRecommendationsDescription")}</p>
      </div>
    );
  }
  
  return (
    <div>
      <h3 className="text-lg font-medium mb-4">{t("comparison.title")}</h3>
      <p className="text-muted-foreground mb-6">{t("comparison.description")}</p>
      
      <Tabs
        value={activeComparisonTab}
        onValueChange={setActiveComparisonTab}
        className="w-full"
      >
        <TabsList className="mb-6 w-full grid grid-cols-2">
          <TabsTrigger value="financial">{t("comparison.tabs.financial")}</TabsTrigger>
          <TabsTrigger value="engineering">{t("comparison.tabs.engineering")}</TabsTrigger>
        </TabsList>
        
        {/* Financial Viability Tab */}
        <TabsContent value="financial">
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.financial.metric")}</th>
                  {recommendations.map((rec, index) => (
                    <th key={index} scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {rec.manufacturer} {rec.model}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.initialCost")}</td>
                  {recommendations.map((rec, index) => {
                    const totalCost = rec.estimatedPrice + (rec.installationCost || rec.estimatedPrice * 0.25) + 500;
                    return (
                      <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(totalCost)}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.annualSavings")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(rec.annualCostSavings + (rec.maintenanceSavings || 250))}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.paybackPeriod")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(rec.paybackPeriod, 2)} {t("common.years")}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.breakEven")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(rec.paybackPeriod * 1.1, 1)} {t("common.years")}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.npv")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(rec.npv)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.irr")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatPercent(rec.irr)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.financial.tenYearCost")}</td>
                  {recommendations.map((rec, index) => {
                    const totalCost = (rec.estimatedPrice + 
                      (rec.installationCost || rec.estimatedPrice * 0.25) + 
                      500) +
                      ((rec.annualEnergySavings * analysis.utilityRate * 10) / 2) +
                      ((analysis.maintenanceCost || 1000) * 10 * 0.5);
                    
                    return (
                      <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(totalCost)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <h4 className="text-base font-medium mb-3">{t("comparison.financial.npvComparison")}</h4>
              <div className="h-72">
                <ResultsCharts
                  type="npvComparison"
                  recommendations={recommendations}
                  analysis={analysis}
                />
              </div>
            </div>
            
            <div>
              <h4 className="text-base font-medium mb-3">{t("comparison.financial.savingsComparison")}</h4>
              <div className="h-72">
                <ResultsCharts
                  type="savingsComparison"
                  recommendations={recommendations}
                  analysis={analysis}
                />
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Engineering Viability Tab */}
        <TabsContent value="engineering">
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.engineering.metric")}</th>
                  {recommendations.map((rec, index) => (
                    <th key={index} scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {rec.manufacturer} {rec.model}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.engineering.compatibilityScore")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(rec.compatibilityScore, 0)}/100
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.engineering.spaceFit")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(rec.spaceFit || rec.compatibilityScore * 1.05, 0)}/100
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.engineering.materialSuitability")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(85, 0)}/100 {/* Assuming a default value if not provided */}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.engineering.controlComplexity")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatNumber(rec.controlComplexity || 90, 0)}/100
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.engineering.systemEfficiency")}</td>
                  {recommendations.map((rec, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatPercent(rec.systemEfficiency)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mb-8">
            <h4 className="text-base font-medium mb-3">{t("comparison.engineering.viabilityRadar")}</h4>
            <div className="h-80">
              <ResultsCharts
                type="engineeringRadar"
                recommendations={recommendations}
                analysis={analysis}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Advanced Analytics Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-medium mb-4">{t("comparison.analytics.title")}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h4 className="text-base font-medium mb-3">{t("comparison.analytics.sensitivityAnalysis")}</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-muted">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.variable")}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">-20%</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.baseCase")}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">+20%</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData?.sensitivityAnalysis && Object.entries(analyticsData.sensitivityAnalysis).map(([variable, values]: [string, any], index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t(`comparison.analytics.variables.${variable}`)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(values.minus20 || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(values.base || recommendations[0]?.npv || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(values.plus20 || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <h4 className="text-base font-medium mb-3">{t("comparison.analytics.carbonReduction")}</h4>
            <div className="h-72">
              <ResultsCharts
                type="emissionsReduction"
                recommendations={recommendations}
                analysis={analysis}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("comparison.analytics.carbonNote")}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-base font-medium mb-3">{t("comparison.analytics.scenarioAnalysis")}</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-muted">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.scenario")}</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.npv")}</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.payback")}</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.riskLevel")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData?.scenarios ? (
                    analyticsData.scenarios.map((scenario: any, index: number) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{scenario.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-muted-foreground">
                          {formatCurrency(scenario.npv)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-muted-foreground">
                          {formatNumber(scenario.payback, 1)} {t("common.years")}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            scenario.risk === "Low" ? "bg-green-100 text-green-800" :
                            scenario.risk === "Medium" ? "bg-yellow-100 text-yellow-800" :
                            "bg-red-100 text-red-800"
                          }`}>
                            {scenario.risk}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.baseCase")}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-muted-foreground">
                          {formatCurrency(recommendations[0]?.npv || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-muted-foreground">
                          {formatNumber(recommendations[0]?.paybackPeriod || 0, 1)} {t("common.years")}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("comparison.analytics.riskLow")}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.risingEnergy")}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                          {formatCurrency((recommendations[0]?.npv || 0) * 1.3)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                          {formatNumber((recommendations[0]?.paybackPeriod || 0) * 0.8, 1)} {t("common.years")}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("comparison.analytics.riskLow")}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.worstCase")}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                          {formatCurrency((recommendations[0]?.npv || 0) * 0.4)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                          {formatNumber((recommendations[0]?.paybackPeriod || 0) * 1.8, 1)} {t("common.years")}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {t("comparison.analytics.riskHigh")}
                          </span>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <h4 className="text-base font-medium mb-3">{t("comparison.analytics.riskAnalysis")}</h4>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.metric")}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("comparison.analytics.value")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData?.monteCarlo ? (
                  <>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.monteCarloMean")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(analyticsData.monteCarlo.mean || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.confidence95")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(analyticsData.monteCarlo.confidence95 || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.var")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(analyticsData.valueAtRisk || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.negativeNPV")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(8.4)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.monteCarloMean")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency((recommendations[0]?.npv || 0) * 0.95)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.confidence95")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency((recommendations[0]?.npv || 0) * 0.6)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.var")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(-(recommendations[0]?.npv || 0) * 0.3)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{t("comparison.analytics.negativeNPV")}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(8.4)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            
            <div className="mt-6">
              <ResultsCharts
                type="sensitivityCurves"
                analyticsData={analyticsData}
                analysis={analysis}
                recommendations={recommendations}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
